import axios from "axios";
import { HANDLE_FILE_SIZE_CHECK } from "../../../helpers/helpers";
import toast from "react-hot-toast";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PATHS } from "../../../constants/paths";
import useApi from "../../../hook/useApi";
import { createFurnitureLeaseMutation } from "../../../api/services/leaseProtectionPolicy.service";

export const useLeaseProtectionListing = () => {
    const navigate = useNavigate();
    const user = JSON.parse(localStorage.getItem('login-user'));
    const [onSubmitTrigger, setOnSubmitTrigger] = useState(false);
    const [customerName, setCustomerName] = useState("");
    const [leaseNo, setLeaseNo] = useState("");
    const [isImageLoading, setIsImageLoading] = useState(false);
    const [isContractLoading, setIsContractLoading] = useState(false);
    const [contractAttachments, setContractAttachments] = useState([]);
    const [leaseProtectionFiles, setLeaseProtectionFiles] = useState([]);
    const [createFurnitureLease, createLeadLoading] = useApi(createFurnitureLeaseMutation);
    // Function to handle file uploads
    // This function takes an array of files, filters them based on size, and uploads them to the server
    const handleLeaseProtectionFiles = (files) => {
        try {
            const validFiles = Array.from(files).filter((file) =>
                HANDLE_FILE_SIZE_CHECK(20, file)
            );

            if (validFiles.length === 0) {
                return;
            }

            setIsImageLoading(true);
            toast.loading('Images are uploading. Please wait...');

            const uploadPromises = validFiles.map((file) => {
                const formData = new FormData();
                formData.append('upload', file);

                return axios
                    .post(
                        `${process.env.REACT_APP_BACKEND_FILE_UPLOAD_URL}/fileUpload`,
                        formData
                    )
                    .then((response) => response.data)
                    .catch((err) => {
                        console.log('Error in uploading the file: ', err);
                        return null;
                    });
            });

            Promise.all(uploadPromises)
                .then((results) => {
                    const successfulUploads = results.filter((result) => result !== null);
                    setLeaseProtectionFiles((prevFiles) => [...prevFiles, ...successfulUploads]);
                    setIsImageLoading(false);
                    toast.dismiss();
                })
                .catch((err) => {
                    console.log('Error in uploading files: ', err);
                    setIsImageLoading(false);
                    toast.dismiss();
                });
        } catch (err) {
            console.log('Error in uploading the files: ', err);
            setIsImageLoading(false);
        }
    };

    // Function to handle contract attachments upload
    const handleContractAttachments = (files) => {
        try {
            const validFiles = Array.from(files).filter((file) =>
                HANDLE_FILE_SIZE_CHECK(20, file)
            );

            if (validFiles.length === 0) {
                return;
            }

            setIsContractLoading(true);
            toast.loading('Contract attachments are uploading. Please wait...');

            const uploadPromises = validFiles.map((file) => {
                const formData = new FormData();
                formData.append('upload', file);

                return axios
                    .post(
                        `${process.env.REACT_APP_BACKEND_FILE_UPLOAD_URL}/fileUpload`,
                        formData
                    )
                    .then((response) => response.data)
                    .catch((err) => {
                        console.log('Error in uploading the file: ', err);
                        return null;
                    });
            });

            Promise.all(uploadPromises)
                .then((results) => {
                    const successfulUploads = results.filter((result) => result !== null);
                    setContractAttachments((prevFiles) => [...prevFiles, ...successfulUploads]);
                    setIsContractLoading(false);
                    toast.dismiss();
                })
                .catch((err) => {
                    console.log('Error in uploading files: ', err);
                    setIsContractLoading(false);
                    toast.dismiss();
                });
        } catch (err) {
            console.log('Error in uploading the files: ', err);
            setIsContractLoading(false);
        }
    };

    // Function to handle image removal
    const handleLeaseProtectionCancel = (index) => {
        setLeaseProtectionFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };

    // Function to handle contract attachment removal
    const handleContractAttachmentCancel = (index) => {
        setContractAttachments((prevFiles) => prevFiles.filter((_, i) => i !== index));
    };
    const saveLeaseProtection = async (e) => {
        e.preventDefault();
        setOnSubmitTrigger(true);
        if (leaseProtectionFiles.length === 0) {
            toast.error('Please upload at least one image');
            return;
        }
        if (contractAttachments.length === 0) {
            toast.error('Please upload at least one file');
            return;
        }
        if (!customerName || customerName === "" || customerName === undefined) {
            toast.error('Customer Name is required');
            return;
        }
        const [firstName = "", ...rest] = customerName.split(" ");
        const lastName = rest.length > 0 ? rest.join(" ") : firstName;
        const createFurnitureLeaseInput = {
            furnitureLease: {
                attachments: JSON.stringify(contractAttachments),
                leaseNo,
                images: JSON.stringify(leaseProtectionFiles),
                entity: {
                    connect: {
                        id: user.entityId, // Replace with the actual entity ID
                    },
                },
            },
            customer: { firstName, lastName },
        };
        try {
            const { data, errors } = await createFurnitureLease(createFurnitureLeaseInput);
            if (data) {
                toast.dismiss();
                toast.success('Lease protection created successfully');
                navigate(PATHS.LEASEPROTECTIONPOLICY);
            } else {
                toast.dismiss();
                toast.error('Failed to create lease protection...try again later');
            }
        } catch (error) {
            console.error('Error creating lease protection:', error);
            toast.dismiss();
            toast.error('An error occurred while creating lease protection');
        } finally { setOnSubmitTrigger(false); }
    };

    return {
        isImageLoading,
        createLeadLoading,
        contractAttachments,
        isContractLoading,
        leaseProtectionFiles,
        onSubmitTrigger,
        handleContractAttachmentCancel,
        handleContractAttachments,
        navigate,
        handleLeaseProtectionFiles,
        handleLeaseProtectionCancel,
        saveLeaseProtection,
        customerName, setCustomerName,
        leaseNo,
        setLeaseNo
    }
}